/**
 * Slide 展示监听 Hook
 * 基于 Intersection Observer 监听 slide 的展示状态，当 slide 完全展示时触发回调
 *
 * @returns {Object} 监听器相关的方法
 */
export function useSlideObserver({ targetAttribute = null }) {
	let intersectionObserver = null;

	/**
	 * 设置 Intersection Observer 监听 slide 的展示状态
	 * 当 slide 完全展示时自动触发回调
	 * @param {Function} callback - 当 slide 完全展示时的回调函数，接收 slideId 作为参数
	 * @param {string} selector - 选择器，用于限定监听范围
	 */
	const setupSlideObserver = (callback, selector) => {
		// 清理旧的 observer
		if (intersectionObserver) {
			intersectionObserver.disconnect();
			intersectionObserver = null;
		}

		// 创建新的 Intersection Observer
		intersectionObserver = new IntersectionObserver(
			entries => {
				entries.forEach(entry => {
					const slideElement = entry.target;
					const slideId = targetAttribute && slideElement.getAttribute(targetAttribute);
					if (!slideId || !callback) {
						return;
					}

					// 当元素完全可见时（intersectionRatio >= 0.95，考虑边界情况）
					const isVisible = entry.isIntersecting && entry.intersectionRatio >= 0.7;
					callback(slideId, isVisible);
				});
			},
			{
				threshold: 0.7,
				rootMargin: '0px',
				root: null
			}
		);

		const slideElements = document.querySelectorAll(selector);
		if (slideElements.length === 0) {
			console.warn(`未找到 ${selector} 元素`);
			return null;
		}

		slideElements.forEach(slide => {
			intersectionObserver.observe(slide);
		});

		return intersectionObserver;
	};

	/**
	 * 清理 Intersection Observer
	 */
	const cleanupSlideObserver = () => {
		if (intersectionObserver) {
			intersectionObserver.disconnect();
			intersectionObserver = null;
		}
	};

	return {
		setupSlideObserver,
		cleanupSlideObserver
	};
}
